import React, {
  createContext,
  useContext,
  useRef,
  useCallback,
  useMemo,
  ReactNode,
} from "react";
import { AutocompleteApi } from "@algolia/autocomplete-js";

interface AutocompleteContextValue {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  autocompleteRef: React.MutableRefObject<AutocompleteApi<any> | null>;
  openSearch: () => void;
  closeSearch: () => void;
  setQuery: (query: string) => void;
}

const AutocompleteContext = createContext<AutocompleteContextValue | null>(
  null
);

/**
 * Provider for Algolia Autocomplete search functionality.
 */
export const AutocompleteProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const autocompleteRef = useRef<AutocompleteApi<any> | null>(null);

  const openSearch = useCallback(() => {
    if (autocompleteRef.current) {
      autocompleteRef.current.setIsOpen(true);
    }
  }, []);

  const closeSearch = useCallback(() => {
    if (autocompleteRef.current) {
      autocompleteRef.current.setIsOpen(false);
    }
  }, []);

  const setQuery = useCallback((query: string) => {
    if (autocompleteRef.current) {
      autocompleteRef.current.setQuery(query);
    }
  }, []);

  const value = useMemo(
    () => ({ autocompleteRef, openSearch, closeSearch, setQuery }),
    [openSearch, closeSearch, setQuery]
  );

  return (
    <AutocompleteContext.Provider value={value}>
      {children}
    </AutocompleteContext.Provider>
  );
};

/**
 * Hook to access Algolia Autocomplete context.
 */
export const useAutocomplete = () => {
  const context = useContext(AutocompleteContext);
  if (!context) {
    throw new Error("useAutocomplete must be used within AutocompleteProvider");
  }
  return context;
};
